import { Archive, Download, Images, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { ExportJob, ExportQuality } from '../lib/api';
import { formatBytes } from '../lib/utils';
import { Dialog, DialogContent, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';

const DOWNLOAD_OPTIONS: Array<{
  quality: ExportQuality;
  title: string;
  description: string;
  action: string;
}> = [
  {
    quality: 'web',
    title: 'Web / digital',
    description: 'Preview-sized images for digital sharing and social media.',
    action: 'Prepare web / digital ZIP'
  },
  {
    quality: 'original',
    title: 'Full quality',
    description: 'Original uploaded files for print and archive delivery.',
    action: 'Prepare full-quality ZIP'
  }
];

export function ExportPanel({
  exportJobs = {},
  creatingQuality,
  onCreate,
  variant = 'card'
}: {
  exportJobs?: Partial<Record<ExportQuality, ExportJob | null>>;
  creatingQuality?: ExportQuality | null;
  onCreate: (quality: ExportQuality) => void;
  variant?: 'card' | 'toolbar';
}) {
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const visibleJobs = DOWNLOAD_OPTIONS.map((option) => ({
    option,
    exportJob: exportJobs[option.quality]
  })).filter(({ exportJob }) => Boolean(exportJob));

  function handleCreate(quality: ExportQuality) {
    onCreate(quality);
    setIsChooserOpen(false);
  }

  return (
    <div className={variant === 'toolbar' ? 'grid gap-3' : 'rounded-sm border border-ink/10 bg-white p-4 shadow-sm'}>
      <div className={variant === 'toolbar' ? 'flex justify-end' : 'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'}>
        {variant === 'card' ? (
          <div>
            <h2 className="text-sm font-semibold text-ink">Gallery downloads</h2>
            <p className="mt-1 text-sm text-ink/70">Choose web/digital or full-quality files when preparing a ZIP.</p>
          </div>
        ) : null}
        <Dialog open={isChooserOpen} onOpenChange={setIsChooserOpen}>
          <DialogTrigger asChild>
            <Button variant={variant === 'toolbar' ? 'ghost' : 'primary'} className="w-fit shrink-0">
              <Download className="h-4 w-4" />
              <span>Download gallery</span>
            </Button>
          </DialogTrigger>
          <DialogContent title="Choose download quality" description="Large galleries are split into ZIP parts when needed.">
            <div className="grid gap-3">
              {DOWNLOAD_OPTIONS.map((option) => (
                <DownloadQualityChoice
                  key={option.quality}
                  option={option}
                  exportJob={exportJobs[option.quality]}
                  isCreating={creatingQuality === option.quality}
                  onCreate={() => handleCreate(option.quality)}
                />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {visibleJobs.length > 0 ? (
        <div className={variant === 'toolbar' ? 'grid gap-3' : 'mt-4 grid gap-3'}>
          {visibleJobs.map(({ option, exportJob }) => (
            <DownloadStatus key={option.quality} option={option} exportJob={exportJob!} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DownloadQualityChoice({
  option,
  exportJob,
  isCreating,
  onCreate
}: {
  option: (typeof DOWNLOAD_OPTIONS)[number];
  exportJob?: ExportJob | null;
  isCreating: boolean;
  onCreate: () => void;
}) {
  const inProgress = exportJob?.status === 'PENDING' || exportJob?.status === 'PROCESSING';
  const failed = exportJob?.status === 'FAILED';
  const canCreate = !isCreating && (!exportJob || failed);
  const buttonLabel = isCreating
    ? 'Preparing'
    : inProgress
      ? 'Processing'
      : exportJob?.status === 'READY'
        ? 'Ready'
        : failed
          ? 'Retry'
          : option.action;

  return (
    <div className="grid gap-3 rounded-sm border border-ink/10 bg-white p-4 shadow-sm">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-smoke text-ink/70">
          {option.quality === 'web' ? <Images className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink">{option.title}</h3>
          <p className="mt-1 text-sm leading-5 text-ink/70">{option.description}</p>
        </div>
      </div>
      <Button onClick={onCreate} disabled={!canCreate} size="sm" className="justify-self-start">
        {isCreating || inProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
        <span>{buttonLabel}</span>
      </Button>
    </div>
  );
}

function DownloadStatus({ option, exportJob }: { option: (typeof DOWNLOAD_OPTIONS)[number]; exportJob: ExportJob }) {
  return (
    <div className="client-gallery-neutral-card grid gap-3 rounded-sm border border-ink/10 bg-bone/55 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink">{option.title}</h3>
          <p className="mt-1 text-sm leading-5 text-ink/70">{option.description}</p>
        </div>
        <span className="w-fit rounded-full bg-white px-2 py-1 text-xs font-medium text-ink/70 shadow-sm ring-1 ring-ink/10">{exportJob.status}</span>
      </div>

      <div className="grid gap-2">
        {exportJob.parts.map((part) => {
          // Compute display label: if part has a sceneName, show "Scene — Part X of Y" per scene grouping
          const sceneName = part.sceneName ?? null;
          let label: string;
          if (sceneName) {
            const sceneParts = exportJob.parts.filter((p) => p.sceneName === sceneName);
            const sceneIndex = sceneParts.findIndex((p) => p.partIndex === part.partIndex);
            const sceneTotal = sceneParts.length;
            label = `${sceneName} — Part ${sceneIndex + 1}${sceneTotal > 1 ? ` of ${sceneTotal}` : ''}`;
          } else {
            label = `Part ${part.partIndex + 1}`;
          }

          return (
            <div key={part.partIndex} className="grid gap-2 rounded-sm border border-ink/10 bg-white p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-ink">{label}</p>
                  <p className="text-ink/70">
                    {part.photoCount} photos · {formatBytes(part.sizeBytes)} · {part.status}
                  </p>
                </div>
                {part.url ? (
                  <Button asChild variant="secondary" size="sm">
                    <a href={part.url}>
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </a>
                  </Button>
                ) : null}
              </div>
              {part.errorMessage ? <p className="text-sm text-clay">{part.errorMessage}</p> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
